import { Button, Empty, Popconfirm, Spin, Tabs, message as antdMessage } from "antd";
import { ArrowLeftOutlined, DeleteOutlined, SendOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { MembersPanel } from "../components/MembersPanel";
import { BulkComposeDrawer } from "../components/BulkComposeDrawer";
import { StatusTable } from "../components/StatusTable";
import { ThreadDrawer } from "../components/ThreadDrawer";
import { deleteGroup, getGroup } from "../api/groups";
import type { GroupDetail } from "../api/groups";
import {
  getCampaign,
  listCampaigns,
} from "../api/campaigns";
import type { CampaignDetail, CampaignSummary } from "../api/campaigns";
import { relativeTime } from "../utils/time";
import { useWs } from "../hooks/useWs";

export function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [activeCampaign, setActiveCampaign] = useState<CampaignDetail | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  const refreshGroup = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { group } = await getGroup(id);
      setGroup(group);
    } finally {
      setLoading(false);
    }
  };

  const refreshCampaigns = async () => {
    if (!id) return;
    const { campaigns } = await listCampaigns(id);
    setCampaigns(campaigns);
    if (!activeId && campaigns.length > 0 && campaigns[0]) {
      setActiveId(campaigns[0].id);
    }
  };

  const refreshActive = async () => {
    if (!activeId) {
      setActiveCampaign(null);
      return;
    }
    try {
      const { campaign } = await getCampaign(activeId);
      setActiveCampaign(campaign);
    } catch {
      setActiveCampaign(null);
    }
  };

  useEffect(() => {
    void refreshGroup();
    void refreshCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void refreshActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useWs((ev) => {
    if (ev.type === "thread:updated") {
      void refreshActive();
      void refreshCampaigns();
    }
  });

  // periodic refresh while a campaign is running
  useEffect(() => {
    if (!activeCampaign || activeCampaign.status === "done") return;
    const t = setInterval(() => {
      void refreshActive();
      void refreshCampaigns();
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaign?.status, activeCampaign?.id]);

  const onDelete = async () => {
    if (!id) return;
    await deleteGroup(id);
    antdMessage.success("已删除");
    navigate("/groups");
  };

  if (loading && !group) {
    return (
      <div style={{ padding: 32, display: "grid", placeItems: "center" }}>
        <Spin />
      </div>
    );
  }

  if (!group) {
    return (
      <div style={{ padding: 32 }}>
        <Empty description="群组不存在" />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button onClick={() => navigate("/groups")}>返回</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/groups")}
            />
            {group.name}
          </span>
        }
        subtitle={`${group.members.length} 个成员 · ${campaigns.length} 次发送记录`}
        extra={
          <div style={{ display: "flex", gap: 8 }}>
            <Popconfirm title="删除该群组?成员关系会一并清除。" onConfirm={onDelete}>
              <Button danger type="text" icon={<DeleteOutlined />} aria-label="删除群组" />
            </Popconfirm>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setComposeOpen(true)}
              disabled={group.members.length === 0}
            >
              新建群发
            </Button>
          </div>
        }
      />

      <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
        <Tabs
          defaultActiveKey="members"
          items={[
            {
              key: "members",
              label: `成员 (${group.members.length})`,
              children: (
                <MembersPanel
                  groupId={group.id}
                  members={group.members}
                  onChange={refreshGroup}
                />
              ),
            },
            {
              key: "campaigns",
              label: `群发活动 (${campaigns.length})`,
              children: (
                <CampaignsPanel
                  campaigns={campaigns}
                  activeId={activeId}
                  setActiveId={setActiveId}
                  activeCampaign={activeCampaign}
                  onChange={refreshActive}
                  onOpenThread={setOpenThreadId}
                />
              ),
            },
          ]}
        />
      </div>

      <BulkComposeDrawer
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        groupId={group.id}
        members={group.members}
        onSent={(cid) => {
          setActiveId(cid);
          void refreshCampaigns();
        }}
      />

      <ThreadDrawer
        threadId={openThreadId}
        open={Boolean(openThreadId)}
        onClose={() => {
          setOpenThreadId(null);
          void refreshActive();
        }}
        onAfterSend={() => void refreshActive()}
        onAfterAction={() => void refreshActive()}
      />
    </>
  );
}

function CampaignsPanel({
  campaigns,
  activeId,
  setActiveId,
  activeCampaign,
  onChange,
  onOpenThread,
}: {
  campaigns: CampaignSummary[];
  activeId: string | null;
  setActiveId: (id: string) => void;
  activeCampaign: CampaignDetail | null;
  onChange: () => void;
  onOpenThread: (threadId: string) => void;
}) {
  if (campaigns.length === 0) {
    return <Empty description="还没有群发活动" />;
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)", gap: 16, alignItems: "flex-start" }}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6, minWidth: 0 }}>
        {campaigns.map((c) => {
          const active = c.id === activeId;
          return (
            <li key={c.id} style={{ minWidth: 0 }}>
              <button
                type="button"
                onClick={() => setActiveId(c.id)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: 10,
                  border: "1px solid",
                  borderColor: active ? "var(--ant-color-primary)" : "var(--ant-color-border)",
                  background: active
                    ? "color-mix(in srgb, var(--ant-color-primary) 8%, transparent)"
                    : "var(--ant-color-bg-container)",
                  borderRadius: 10,
                  cursor: "pointer",
                  color: "inherit",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.subject || "(无主题)"}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ant-color-text-secondary)",
                    marginTop: 2,
                  }}
                >
                  {relativeTime(c.createdAt)} · {c.recipientCount} 收件人 · {c.status}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      <div>
        {activeCampaign ? (
          <StatusTable
            campaign={activeCampaign}
            onChange={onChange}
            onOpenThread={onOpenThread}
          />
        ) : (
          <Empty description="选择左侧活动查看状态" />
        )}
      </div>
    </div>
  );
}
